import React, { useEffect, useState } from "react";
import {
  TextField,
  MenuItem,
  InputAdornment,
  Select,
  styled,
} from "@mui/material";
import Box from "@mui/material/Box";
import InputMask from "react-input-mask";
import { useSelector } from "react-redux";
import { selectCountries } from "@/store/slices/general/countriesSlice";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { cleanedPhoneNumber } from "@/services/api-data-mapping";
import { getPhoneValue } from "@/services/generic.methods";

const TextInput = React.forwardRef(function TextInput(props, ref) {
  return <InputMask {...props} inputRef={ref} />;
});

const UIPhoneWithCountryPicker = ({
  name,
  title = "",
  value,
  disabled = false,
  onChange,
  onBlur = () => {},
  error,
  selectValue,
  selectName,
  clearable = true,
  infoText = "",
  size = "small",
  required = false,
  endAdornment = null,
  showErrorText = false,
}) => {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const countries = useSelector(selectCountries) || [];

  const handleCountryChange = (event) => {
    const selectedCode = event.target.value;
    const temp = (countries?.countries || []).find(
      (country) => selectedCode == country.code
    );

    const newValue = (selectedCode === "" || cleanedPhoneNumber(value)) ? "" : temp?.dial_code;

    onChange(
      { 
        ...event, 
        target: { 
          ...event.target, 
          value: newValue, 
          name: name
        } 
      },
      temp?.code
    );
  };

  useEffect(() => {
    if (selectValue === "") {
      setSelectedCountry(null);
    } else {
      const country = (countries?.countries || []).find(
        (country) =>
          selectValue == country.code || selectValue == country.dial_code
      );
      setSelectedCountry(country);
    }
  }, [selectValue, disabled, countries]);

  return (
    <Box
      sx={{
        bgcolor: disabled
          ? "var(--palette-disabled_button)"
          : "var(--palette-grid_table)",
        position: "relative",
      }}
    >
      <TextField
        label={title + (required ? " *" : "")}
        name={name}
        value={getPhoneValue(value, selectedCountry)}
        size={size}
        fullWidth
        variant={"outlined"}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled || !selectedCountry}
        // error={error}
        InputLabelProps={{
          style: {
            color: error
              ? "#d32f2f"
              : disabled
                ? "var(--palette-disabled_button_text)"
                : "var(--palette-grey_text)",
          },
        }}
        InputProps={{
          sx: { paddingLeft: "0px", color: "var(--palette-text)" },
          // inputComponent: InputMask,
          inputComponent: TextInput,
          inputProps: {
            mask: selectedCountry?.phone_mask,
          },
          startAdornment: (
            <InputAdornment position="start">
              <Box
                sx={{
                  py: 0.5,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Select
                  name={selectName}
                  value={selectedCountry?.code || ""}
                  onChange={handleCountryChange}
                  disabled={disabled}
                  sx={{
                    minWidth: "110px",
                    width: "110px",
                    height: "28px",
                  "& .Mui-disabled": {
                    color: "var(--palette-disabled_button_text)",
                    WebkitTextFillColor: "var(--palette-disabled_button_text)",
                  },
                  "& .MuiSelect-icon": {
                    color: "var(--palette-text)",
                  },
                  "& fieldSet": {
                    borderLeft: "none",
                    borderTop: "none",
                    borderBottom: "none",
                    borderRadius: "unset",
                  },
                  "& .MuiSelect-select": {
                    color: "var(--palette-text)", // Set the desired color for the underline here
                  },
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  // getContentAnchorEl: null,
                  PaperProps: {
                    className: "custom-scroll",
                    style: {
                      maxHeight: "250px",
                    },
                  },
                  // displayEmpty: true,
                }}
                renderValue={() => (
                  <span>
                    {selectedCountry ? (
                      <>
                        <span
                          className={`fi fi-${selectedCountry.code.toLowerCase()}`}
                        ></span>
                        &nbsp; {selectedCountry.dial_code}
                      </>
                    ) : (
                      "Select"
                    )}
                  </span>
                )}
              >
                {clearable ? <MenuItem value="">Select</MenuItem> : null}
                {(countries?.countries || []).map((country) => (
                  <MenuItem
                    key={country.code}
                    value={country.code}
                    onClick={() => {
                      // handleCountryChange(country)
                    }}
                  >
                    <span
                      className={`fi fi-${country.code.toLowerCase()}`}
                    ></span>
                    &nbsp; {country.name} ({country.dial_code})
                  </MenuItem>
                ))}
                </Select>
              </Box>
            </InputAdornment>
          ),
          endAdornment:
            endAdornment ? (
              <InputAdornment position="end">{endAdornment}</InputAdornment>
            ) : infoText && infoText.length ? (
              <InputAdornment position="end">
                <CustomWidthTooltip title={infoText} placement="top-end">
                  <InfoOutlinedIcon color="action" sx={{ fontSize: 18 }} />
                </CustomWidthTooltip>
              </InputAdornment>
            ) : null,
        }}
        FormHelperTextProps={{
          sx: {
            bgcolor: "var(--palette-grid_table)",
            px: 0.5,
            mx: 1,
            mt: 0.5,
            lineHeight: 1.4,
            whiteSpace: "normal",
          },
        }}
        error={!!error}
        helperText={
          showErrorText
            ? (typeof error === "string" && error.trim() ? error : "\u00A0")
            : (typeof error === "string" && error.trim() ? error : undefined)
        }
      />
    </Box>
  );
};

export default UIPhoneWithCountryPicker;

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 200,
  },
});
